import { notification, Spin } from 'antd'
import { t } from 'i18next'
import React, { createContext, useCallback, useEffect, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { useLocalStorage } from 'react-use'
import { get, post } from '../Common/api'
import { debounce } from '../Common/debounce'
import eventBus from '../Common/EventBus'
import { lbmNotification } from '../Common/notification'
import consumer from '../Common/cable'
import dayjs from 'dayjs'

const GlobalContext = createContext()
export default GlobalContext

export const GlobalContextProvider = ({ children }) => {
  const [user, setUser] = useLocalStorage('user', { isAuth: false })
  const [boxes, setBoxes] = useLocalStorage('boxes', [])
  const [slots, setSlots] = useLocalStorage('slots', [])
  const [owners, setOwners] = useLocalStorage('owners', [])
  const [events, setEvents] = useLocalStorage('events', [])
  const [inCharge, setInCharge] = useLocalStorage('inCharge', [])
  const [filters, setFilters] = useLocalStorage('filters', [])
  const [tags, setTags] = useLocalStorage('tags', [])
  const [box, setBox] = useState()
  const [slot, setSlot] = useState()
  const [event, setEvent] = useState()
  const [loading, setLoading] = useState(false)
  const [eventOn, setEventOn] = useState(false)
  const [notificationOn, setNotificationOn] = useState(false)
  const [notificationApi, notificationContextHolder] = notification.useNotification()

  // Slots state
  const [slotsDate, setSlotsDate] = useState(dayjs())

  const navigate = useNavigate()

  const onNotAuthenticated = useCallback(debounce(() => {
    navigate('/login')
    logout()
  }, 100), [])

  useEffect(() => {
    if (eventOn) { return }
    setEventOn(true)
    eventBus.on('NOT_AUTHENTICATED', onNotAuthenticated)
    return () => {
      eventBus.remove('NOT_AUTHENTICATED', onNotAuthenticated)
    }
  }, [])

  useEffect(() => {
    if (user.isAuth) {
      const boxSubscription = consumer.subscriptions.create({ channel: 'BoxesChannel' }, {
        connected: () => { console.log('box connected') },
        disconnected: () => { },
        received: (res) => { setBox(res.box) }
      })
      const slotSubscription = consumer.subscriptions.create({ channel: 'SlotsChannel' }, {
        connected: () => { console.log('slot connected') },
        disconnected: () => { },
        received: (res) => { setSlot(res.slot) }
      })
      const eventSubscription = consumer.subscriptions.create({ channel: 'EventsChannel' }, {
        connected: () => { console.log('event connected') },
        disconnected: () => { },
        received: (res) => { setEvent({ ...res, content: res.content_trix_html }) }
      })
      let notifSubscription = false
      if (!notificationOn) {
        setNotificationOn(true)
        notifSubscription = consumer.subscriptions.create({ channel: 'NotifyChannel' }, {
          connected: () => { console.log('notif connected') },
          disconnected: () => { },
          received: (res) => { lbmNotification(navigate, res, notificationApi) }
        })
      }
      getBoxes()
      getInCharge()
      getOwners()
      getFilters()
      getTags()
      return () => {
        boxSubscription.unsubscribe()
        slotSubscription.unsubscribe()
        eventSubscription.unsubscribe()
        if (notifSubscription) {
          notifSubscription.unsubscribe()
        }
      }
    }
  }, [consumer])

  useEffect(() => {
    if (box) {
      updateBoxes([box])
    }
  }, [box])

  useEffect(() => {
    if (slot) {
      updateSlots([slot])
    }
  }, [slot])

  useEffect(() => {
    if (event) {
      updateEvents([event])
    }
  }, [event])

  const login = async (params) => {
    setLoading(true)
    await post({
      url: 'admin/login',
      params,
      notification: { api: notificationApi, state: 'login' },
      onSuccess: (response) => {
        const url = params.fabrication ? '/commands' : '/admin'
        setUser({ isAuth: true, ...response.user })
        notification.success({ message: response.message, placement: 'bottom' })
        window.location.href = url
      },
      onFailure: (response) => {
        notification.error({ message: response.error, placement: 'bottom' })
      }
    })
    setLoading(false)
  }

  const logout = () => {
    setUser({ isAuth: false })
    setBoxes([])
    setSlots([])
    setOwners([])
    setEvents([])
    setInCharge([])
    setFilters([])
    setTags([])
  }

  const updatePassword = async (params) => {
    setLoading(true)
    await post({
      url: 'admin/users/update_password',
      params,
      notification: { api: notificationApi, state: 'password_update' },
      onSuccess: () => {
        navigate('/admin/login')
      }
    })
    setLoading(false)
  }

  const checkToken = async (params) => {
    setLoading(true)
    await post({
      url: 'admin/users/update_password',
      params,
      onSuccess: () => {},
      onFailure: (response) => {
        if (Object.keys(response.errors).includes('reset_password_token')) {
          notificationApi.error({ key: 'request_response', message: 'Lien expiré', placement: 'bottom' })
          navigate('/admin/login')
        }
      }
    })
    setLoading(false)
  }

  const errorMessages = (errors, traductionPath) => {
    return Object.keys(errors).map(key => (
      <>{t(traductionPath + '.' + key) + ': ' + errors[key]}</>
    ))
  }

  const getSlots = async (date) => {
    setLoading(true)
    await get({
      url: 'admin/slots',
      params: { scope: 'filtered_list', date: date.format('YYYY-MM-DD') },
      onSuccess: (res) => {
        updateSlots(res)
        setLoading(false)
      }
    })
  }

  const updateSlots = (newSlots) => {
    let savedSlots = slots || []
    newSlots = newSlots.map((slot) => ({ ...slot, assigned_short_name: !slot.assigned_short_name ? '' : slot.assigned_short_name }))
    newSlots.forEach((slot) => {
      const index = savedSlots.findIndex((b) => b.id === slot.id)
      if (index !== -1) {
        savedSlots[index] = slot
      } else {
        savedSlots = [...savedSlots, slot]
      }
    })
    setSlots([...savedSlots])
  }

  const getUnresolvedEvents = async () => {
    await get({
      url: 'admin/events',
      params: { resolved: false },
      notification: { api: notificationApi, state: 'get' },
      onSuccess: (res) => {
        updateEvents(res)
      }
    })
  }

  const getEvents = async (eventFilter) => {
    await get({
      url: 'admin/events',
      params: { ...eventFilter },
      onSuccess: (res) => {
        updateEvents(res)
      }
    })
  }

  const updateEvents = (newEvents) => {
    let savedEvents = events || []
    newEvents.forEach((event) => {
      const index = savedEvents.findIndex((b) => b.id === event.id)
      if (index !== -1) {
        savedEvents[index] = event
      } else {
        savedEvents = [...savedEvents, event]
      }
    })
    setEvents([...savedEvents])
  }

  const getBoxes = async () => {
    await get({
      url: 'admin/boxes',
      params: {},
      notification: { api: notificationApi, state: 'get' },
      onSuccess: (res) => {
        updateBoxes(res)
      }
    })
  }

  const updateBoxes = (newBoxes) => {
    let savedBoxes = boxes || []
    newBoxes.forEach((box) => {
      const index = savedBoxes.findIndex((b) => b.id === box.id)
      if (index !== -1) {
        savedBoxes[index] = box
      } else {
        savedBoxes = [...savedBoxes, box]
      }
    })
    setBoxes([...savedBoxes])
  }

  const getInCharge = async () => {
    await get({
      url: 'admin/events/in_charge_options',
      notification: { api: notificationApi, state: 'get' },
      params: {},
      onSuccess: (res) => {
        setInCharge(res)
      }
    })
  }

  const getOwners = async () => {
    await get({
      url: 'admin/owners',
      notification: { api: notificationApi, state: 'get' },
      params: {},
      onSuccess: (res) => {
        setOwners(res)
      }
    })
  }

  const getFilters = async () => {
    await get({
      url: 'admin/event_filters/user',
      notification: { api: notificationApi, state: 'get' },
      params: { user_id: user.id },
      onSuccess: (res) => {
        setFilters(res)
      }
    })
  }

  const updateFilters = (filter) => {
    let savedFilters = filters || []
    const index = filters.findIndex((b) => b.id === filter.id)
    if (index !== -1) {
      savedFilters[index] = filter
    } else {
      savedFilters = [...savedFilters, filter]
    }
    setFilters([...savedFilters])
  }

  const getTags = async () => {
    await get({
      url: 'admin/tags/all',
      notification: { api: notificationApi, state: 'get' },
      params: {},
      onSuccess: (res) => {
        setTags(res)
      }
    })
  }

  const values = {
    // localStorage
    user,
    setUser,
    slots,
    boxes,
    owners,
    events,
    setEvents,
    inCharge,
    filters,
    tags,

    // getter
    getSlots,
    getBoxes,
    getOwners,
    getEvents,
    getUnresolvedEvents,
    getFilters,
    getTags,

    // updater
    updateSlots,
    updateBoxes,
    updateEvents,
    updateFilters,

    // methods
    login,
    logout,
    updatePassword,
    checkToken,
    errorMessages,
    notificationApi,
    slotsDate,
    setSlotsDate
  }

  return <GlobalContext.Provider value={values}>
    <Spin spinning={loading}>
      {notificationContextHolder}
      { children || <Outlet /> }
    </Spin>
  </GlobalContext.Provider>
}
